import React, { useEffect } from 'react';
import { Modal } from './Modal';

interface ConfirmModalProps {
  open: boolean;
  message: React.ReactNode;
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * `'card'` (default) renders the desktop centred card via <Modal>.
   * `'sheet'` renders a mobile-native bottom sheet that matches the visual
   * language of MobileInventoryIssueSheet / MobileInventoryReturnSheet /
   * profile sheet (slide-up, handle bar, full-width buttons at bottom).
   */
  variant?: 'card' | 'sheet';
}

export function ConfirmModal({
  open, message, title,
  confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  destructive = false, onConfirm, onCancel,
  variant = 'card',
}: ConfirmModalProps) {
  if (variant === 'sheet') {
    return <ConfirmSheet
      open={open} message={message} title={title}
      confirmLabel={confirmLabel} cancelLabel={cancelLabel}
      destructive={destructive} onConfirm={onConfirm} onCancel={onCancel}
    />;
  }
  return (
    <Modal
      open={open}
      onClose={onCancel}
      size="sm"
      title={title}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onCancel}>{cancelLabel}</button>
          <button
            className={`btn ${destructive ? 'btn-danger' : 'btn-primary'}`}
            onClick={onConfirm}
            autoFocus
          >{confirmLabel}</button>
        </>
      }
    >
      <div className="text-base" style={{ color: 'var(--text)', lineHeight: 1.6 }}>{message}</div>
    </Modal>
  );
}

// ── Mobile sheet variant ────────────────────────────────────────────
// Slides up from the bottom over a tap-to-dismiss backdrop. Uses the
// `m-sheet-backdrop` / `m-sheet-content` keyframes already injected by
// MobileLayout's MOBILE_CSS so the entry animation matches every other
// mobile sheet. On close (cancel, backdrop tap, or confirm) we play the
// `m-sheet-closing` reverse animation before unmounting via onCancel/
// onConfirm — the parent owns the open/close state.
type ConfirmSheetProps = Omit<ConfirmModalProps, 'variant'> & {
  // After defaulting in ConfirmModal these are always strings/booleans.
  confirmLabel: string;
  cancelLabel: string;
  destructive: boolean;
};

function ConfirmSheet({
  open, message, title,
  confirmLabel, cancelLabel,
  destructive, onConfirm, onCancel,
}: ConfirmSheetProps) {
  const [closing, setClosing] = React.useState(false);

  // Lock body scroll while the sheet is up (matches Modal behaviour).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Reset the closing flag whenever the sheet re-opens.
  useEffect(() => { if (open) setClosing(false); }, [open]);

  if (!open) return null;

  const playClose = (after: () => void) => {
    setClosing(true);
    setTimeout(after, 350);
  };

  const handleCancel  = () => playClose(onCancel);
  const handleConfirm = () => playClose(onConfirm);

  return (
    <div
      className={closing ? 'm-sheet-closing' : 'm-sheet-backdrop'}
      style={{
        position: 'fixed', inset: 0, zIndex: 500,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
      }}
      onClick={handleCancel}
      role="dialog" aria-modal="true"
    >
      <div
        className="m-sheet-content"
        style={{
          background: 'var(--surface)',
          borderRadius: '20px 20px 0 0',
          maxHeight: '50vh',
          paddingBottom: 'env(safe-area-inset-bottom, 16px)',
          display: 'flex', flexDirection: 'column',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Handle bar — same dimensions as every other mobile sheet */}
        <div
          aria-hidden
          style={{
            width: 36, height: 4, background: 'var(--text3)', opacity: 0.3,
            borderRadius: 2, margin: '12px auto 4px',
          }}
        />

        {/* Body */}
        <div style={{ padding: '14px 20px 20px' }}>
          {title && (
            <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>{title}</div>
          )}
          <div style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--text2)' }}>{message}</div>
        </div>

        {/* Buttons — full-width row, Cancel left, Confirm right */}
        <div style={{ display: 'flex', gap: 8, padding: '0 20px 20px' }}>
          <button
            className="btn btn-ghost"
            onClick={handleCancel}
            style={{ flex: 1, minHeight: 48, fontSize: 15 }}
          >{cancelLabel}</button>
          <button
            className={`btn ${destructive ? 'btn-danger' : 'btn-primary'}`}
            onClick={handleConfirm}
            autoFocus
            style={{ flex: 1, minHeight: 48, fontSize: 15 }}
          >{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
